import _                   from 'lodash'
import { delegate, wrap }  from 'alto-utils/lib/macros'
import AssociationInjector from './association_injector'
import AssociationFinder   from './association_finder'
import EntitySpace         from './entity_space'
import EntityMap           from './entity_map'

export default class EntityCollection
  delegate(@, 'map', 'flatMap', 'reduce', 'forEach',
           'find',   'findOrNullEntity',   'safeFind',
           'findBy', 'findByOrNullEntity', 'safeFindBy',
           'first', 'safeFirst', 'size', 'pkeysArray', 'nullEntity',
           to: 'entityMap')

  wrap(@, 'where', 'orderBy', around: 'entityMap', wrapperMethod: '_withMap')

  constructor: (@entityMap, @model, @entitySpace, @associationInjector) ->
    @associationFinder = new AssociationFinder(@associationInjector.associations)

  @with: (entities, model, entitySpace, injector) ->
    entityMap = EntityMap.for(entities, model, injector)

    new @(entityMap, model, entitySpace, injector)

  @for: (entities, model, associations) ->
    @_single(entities, model, associations)

  @singletonFor: (entity, associations) ->
    @_single([entity], entity.constructor, associations)

  @emptyFor: (model, associations) ->
    @_single([], model, associations)

  @_single: (entities, model, associations) ->
    entitySpace = EntitySpace.for(model, entities)
    injector    = AssociationInjector.for(entitySpace, associations, @)
    entityMap   = EntityMap.for(entities, model, injector)

    new @(entityMap, model, entitySpace, injector)

  pkeys:       -> @entityMap.pkeysList()
  entities:    -> @entityMap.toList()
  entitySet:   -> @entityMap.toSet()
  entityArray: -> @entityMap.toArray()

  join: (other) ->
    @_withSpace(@entitySpace.merge(other.entitySpace))

  union: (other) ->
    space    = @entitySpace.merge(other.entitySpace)
    injector = @associationInjector.withSpace(space)
    map      = @entityMap.union(other.entityMap).withInjector(injector)

    new @constructor(map, @model, space, injector)

  # NOTE: what if other's entities' associations have different entity space?
  intersect: (other) ->
    @_withMap(@entityMap.intersect(other.entityMap))

  # NOTE: what if other's entities' associations have different entity space?
  subtract: (other) ->
    @_withMap(@entityMap.subtract(other.entityMap))

  @union: (collections) ->
    unless collections.length
      throw new Error('EntityCollection.union must receive at least one collection')

    _.reduce(collections, (running, current) -> running.union(current))

  @intersect: (collections) ->
    unless collections.length
      throw new Error('EntityCollection.intersect must receive at least one collection')

    _.reduce(collections, (running, current) -> running.intersect(current))

  add: (entity) ->
    space     = @entitySpace.add(entity)
    injector  = @associationInjector.withSpace(space)
    entityMap = @entityMap.add(entity).withInjector(injector)

    new @constructor(entityMap, @model, space, injector)

  update: (entity) ->
    space     = @entitySpace.update(entity)
    injector  = @associationInjector.withSpace(space)
    entityMap = @entityMap.update(entity).withInjector(injector)

    new @constructor(entityMap, @model, space, injector)

  # for now expects all associations to cascade on delete
  remove: (pkOrEntity) ->
    pk     = if _.isObject(pkOrEntity) then pkOrEntity.pk else pkOrEntity
    entity = @safeFind(pk)

    return @ unless entity

    belongingEntities = @associationFinder.belongingEntitiesOf(entity)
    removeReducer     = (collection, entity) -> collection._remove(entity)

    belongingEntities.add(entity).reduce(removeReducer, @)

  secondaryFor:     (model) -> @_withMap(@entitySpace.mapFor(model), model)
  safeSecondaryFor: (model) -> @_withMap(@entitySpace.safeMapFor(model), model)

  addSecondary:    (entity) -> @_withSpace(@entitySpace.add(entity))
  updateSecondary: (entity) -> @_withSpace(@entitySpace.update(entity))

  removeSecondary: (pk, model) ->
    entity = @entitySpace.safeEntityFor(model, pk)

    return @ unless entity

    withAssociations  = @associationInjector.inject(entity)
    belongingEntities = @associationFinder.belongingEntitiesOf(withAssociations)
    removeReducer     = (collection, entity) -> collection._removeSecondary(entity)

    belongingEntities.add(entity).reduce(removeReducer, @)

  groupBy: (grouper) ->
    @entityMap.groupBy(grouper).map (entityMap) => @_withMap(entityMap)

  _withMap: (entities, model = @model) ->
    entityMap = EntityMap.for(entities, model, @associationInjector)

    new @constructor(entityMap, model, @entitySpace, @associationInjector)

  _withSpace: (entitySpace) ->
    injector  = @associationInjector.withSpace(entitySpace)
    entityMap = @entityMap.withInjector(injector)

    new @constructor(entityMap, @model, entitySpace, injector)

  _remove: (entity) ->
    space     = @entitySpace.remove(entity)
    injector  = @associationInjector.withSpace(space)
    entityMap = if entity instanceof @model then @entityMap.remove(entity) else @entityMap
    entityMap = entityMap.withInjector(injector)

    new @constructor(entityMap, @model, space, injector)

  _removeSecondary: (entity) ->
    @_withSpace(@entitySpace.remove(entity))
